// src/services/depositService.js - SECURED
import { API_BASE_URL } from '../utils/constants';

const getAuthHeaders = (contentType = 'application/json') => {
  return {
    'Content-Type': contentType,
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/';
    }
    const error = await response.json();
    throw new Error(error.error || error.detail || 'Request failed');
  }
  return response.json();
};

export const depositService = {
  async getDepositSettings() {
    try {
      const url = `${API_BASE_URL}/deposit/settings/`;
      console.log('🔄 Fetching deposit settings from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const result = await handleResponse(response);
      console.log('✅ Deposit settings received:', result);
      return result;
    } catch (error) {
      console.error('💥 Error fetching deposit settings:', error);
      throw error;
    }
  },

  async getDepositDescription() {
    try {
      const url = `${API_BASE_URL}/deposit/deposits/description/`;
      console.log('🔄 Fetching deposit description from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const result = await handleResponse(response);
      console.log('✅ Deposit description received:', result);
      return result;
    } catch (error) {
      console.error('💥 Error fetching deposit description:', error);
      throw error;
    }
  },

  async getBankAccounts() {
    try {
      const url = `${API_BASE_URL}/deposit/bank-accounts/`;
      console.log('🔄 Fetching bank accounts from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const result = await handleResponse(response);
      console.log('✅ Bank accounts received:', result);
      return result;
    } catch (error) {
      console.error('💥 Error fetching bank accounts:', error);
      throw error;
    }
  },

  async getCryptoWallets(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_BASE_URL}/deposit/deposits/crypto/wallets/${queryString ? `?${queryString}` : ''}`;
      console.log('📄 Fetching crypto wallets from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const result = await handleResponse(response);
      console.log('✅ Crypto wallets response:', result);
      return result;
    } catch (error) {
      console.error('💥 Error fetching crypto wallets:', error);
      throw error;
    }
  },

  async createCryptoDeposit(formData) {
    try {
      console.log('📄 Creating crypto deposit...');
      const url = `${API_BASE_URL}/deposit/deposits/crypto/`;
      console.log('🌐 Making request to:', url);
      console.log('📝 Sending data:', formData);
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('💥 Error creating crypto deposit:', error);
      throw error;
    }
  },

  async createBankDeposit(formData) {
    try {
      console.log('📄 Creating bank deposit...');
      const url = `${API_BASE_URL}/deposit/deposits/bank-transfer/`;
      console.log('🌐 Making request to:', url);
      console.log('📝 Sending data:', formData);
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('💥 Error creating bank deposit:', error);
      throw error;
    }
  },

  async getUserDeposits() {
    try {
      const url = `${API_BASE_URL}/deposit/deposits/`;
      console.log('📄 Fetching user deposits from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('💥 Error fetching user deposits:', error);
      throw error;
    }
  },

  async getWalletInfo() {
    try {
      const url = `${API_BASE_URL}/deposit/wallet/`;
      console.log('📄 Fetching wallet info from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('💥 Error fetching wallet info:', error);
      throw error;
    }
  },

  async getDepositHistory(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_BASE_URL}/deposit/deposits/${queryString ? `?${queryString}` : ''}`;
      console.log('📄 Fetching deposit history from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('💥 Error fetching deposit history:', error);
      throw error;
    }
  },

  async getPendingDeposits() {
    try {
      const url = `${API_BASE_URL}/deposit/deposits/pending/`;
      console.log('📄 Fetching pending deposits from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('💥 Error fetching pending deposits:', error);
      throw error;
    }
  },

  async getDepositStatistics() {
    try {
      const url = `${API_BASE_URL}/deposit/statistics/`;
      console.log('📄 Fetching deposit statistics from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('💥 Error fetching deposit statistics:', error);
      throw error;
    }
  },

  async cancelDeposit(depositId) {
    try {
      const url = `${API_BASE_URL}/deposit/deposits/${depositId}/cancel/`;
      console.log('📄 Canceling deposit:', depositId);
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('💥 Error canceling deposit:', error);
      throw error;
    }
  },

  async getDepositMethods() {
    try {
      const url = `${API_BASE_URL}/deposit/methods/`;
      console.log('📄 Fetching deposit methods from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('💥 Error fetching deposit methods:', error);
      throw error;
    }
  }
};

export default depositService;